import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBeforeUnload, useLocation, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { useReviewerLogic } from '../../hooks/useReviewerLogic';

import ConfigPanel from '../../components/CreateReviewer/ConfigPanel'; 
import InputSection from '../../components/CreateReviewer/InputSection';
import ResultsDashboard from '../../components/CreateReviewer/ResultsDashboard';
import GenerationProgress from '../../components/CreateReviewer/GenerationProgress';

const CreateReviewer = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('file');
  const location = useLocation();
  const navigate = useNavigate();
  const isConfirmOpenRef = useRef(false);
  
  const { state, actions } = useReviewerLogic();
  const shouldWarnUnsaved =
    state.view === "dashboard" &&
    !!state.generatedData &&
    !state.hasSavedToDashboard &&
    !state.isSaving;

  useEffect(() => {
    const prefillFolderId =
      location.state?.prefillFolderId ||
      new URLSearchParams(location.search).get("folderId") ||
      "";
    const prefillInputMode = location.state?.prefillInputMode || "";

    if (prefillFolderId) actions.setFolderId(prefillFolderId);
    if (prefillInputMode === "file" || prefillInputMode === "text") {
      setActiveTab(prefillInputMode);
    }
  }, [location.state, location.search]);

  useBeforeUnload((event) => {
    if (!shouldWarnUnsaved) return;
    event.preventDefault();
    // Required for browser native leave prompt.
    event.returnValue = "";
  });

  const handleBack = useCallback(() => {
    if (shouldWarnUnsaved) {
      if (isConfirmOpenRef.current) return;
      isConfirmOpenRef.current = true;
      Swal.fire({
        icon: "warning",
        title: "Review Not Saved",
        text: "The review has not been saved. If you leave or create a new one, your generated reviewer will be lost.",
        showCancelButton: true,
        confirmButtonText: "Leave Without Saving",
        cancelButtonText: "Stay & Save",
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#3b82f6",
        reverseButtons: true,
      })
        .then((result) => {
          if (result.isConfirmed) {
            actions.resetForm();
          }
        })
        .finally(() => {
          isConfirmOpenRef.current = false;
        });
    } else {
      actions.resetForm();
    }
  }, [shouldWarnUnsaved, actions]);

  useEffect(() => {
    if (!shouldWarnUnsaved) return;

    const onDocumentClick = (event) => {
      const anchor = event.target?.closest?.("a[href]");
      const button = event.target?.closest?.("button");

      // Check if user clicked an anchor link (Sidebar navigation, Header links)
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href || href.startsWith("#") || anchor.hasAttribute("download") || anchor.getAttribute("target") === "_blank") return;

        const nextUrl = new URL(href, window.location.origin);
        if (nextUrl.origin !== window.location.origin) return;

        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const next = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        if (current === next) return;
        if (isConfirmOpenRef.current) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        isConfirmOpenRef.current = true;

        Swal.fire({
          icon: "warning",
          title: "Review Not Saved",
          text: "The review has not been saved. If you leave this page, your generated reviewer will be lost.",
          showCancelButton: true,
          confirmButtonText: "Leave Without Saving",
          cancelButtonText: "Stay & Save",
          confirmButtonColor: "#ef4444",
          cancelButtonColor: "#3b82f6",
          reverseButtons: true,
        })
          .then((result) => {
            if (result.isConfirmed) {
              navigate(next);
            }
          })
          .finally(() => {
            isConfirmOpenRef.current = false;
          });
        return;
      }

      // Check if user clicked a button
      if (button) {
        // Allow clicks on "Save to Dashboard", "Download PDF", and interactive dashboard items (flashcards/quiz)
        const isSaveBtn = button.textContent?.includes("Save to Dashboard");
        const isDownloadBtn = button.textContent?.includes("Download PDF");
        const isInteractive = button.closest?.(".dashboard-interactive") || button.closest?.(".quiz-editor");
        if (isSaveBtn || isDownloadBtn || isInteractive) return;

        // If it's the "Create New" button
        if (button.textContent?.includes("Create New")) {
          event.preventDefault();
          event.stopPropagation();
          handleBack();
          return;
        }

        // If it's a mobile back button in Header
        if (button.closest?.("header") && button.querySelector?.(".fa-arrow-left")) {
          event.preventDefault();
          event.stopPropagation();
          handleBack();
          return;
        }

        // If it's an outside navigation button in Sidebar or Header (exclude sidebar toggling)
        if (button.closest?.("aside") || button.closest?.("header")) {
          if (button.getAttribute?.("title") === "Toggle Menu" || button.querySelector?.(".fa-bars") || button.querySelector?.(".fa-xmark")) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          if (isConfirmOpenRef.current) return;
          isConfirmOpenRef.current = true;

          Swal.fire({
            icon: "warning",
            title: "Review Not Saved",
            text: "The review has not been saved. If you leave, your generated reviewer will be lost.",
            showCancelButton: true,
            confirmButtonText: "Leave Without Saving",
            cancelButtonText: "Stay & Save",
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#3b82f6",
            reverseButtons: true,
          }).finally(() => {
            isConfirmOpenRef.current = false;
          });
        }
      }
    };

    document.addEventListener("click", onDocumentClick, true);
    return () => {
      document.removeEventListener("click", onDocumentClick, true);
    };
  }, [navigate, shouldWarnUnsaved, handleBack]);

  return (
    <div className="bg-slate-50 text-slate-800 font-sans flex h-screen overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      
      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header 
          toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} 
          title={state.view === 'form' ? 'Create Reviewer' : state.title} 
          onBack={shouldWarnUnsaved ? handleBack : undefined}
        />
        
        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-6xl mx-auto">
            
            {/* --- FORM VIEW --- */}
            {state.view === 'form' && (
              <form 
                onSubmit={(e) => { e.preventDefault(); actions.handleGenerate(activeTab); }} 
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-fadeIn"
              >
                {/* Left Col: Inputs + Desktop Generate Button */}
                <div className="lg:col-span-8 space-y-6">
                  <InputSection 
                    title={state.title} setTitle={actions.setTitle}
                    folderId={state.folderId} setFolderId={actions.setFolderId}
                    folders={state.folders} loadingFolders={state.foldersLoading}
                    activeTab={activeTab} setActiveTab={setActiveTab}
                    textInput={state.textInput} setTextInput={actions.setTextInput}
                    uploadedFiles={state.uploadedFiles} setUploadedFiles={actions.setUploadedFiles}
                  />
                  {/* Desktop Generate Reviewer Button (Hidden on mobile) */}
                  <button 
                    type="submit" 
                    disabled={state.isProcessing} 
                    className="hidden lg:flex w-full bg-blue-600 text-white py-3.5 rounded-xl font-bold hover:shadow-lg hover:bg-blue-700 active:scale-[0.98] transition-all items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {state.isProcessing ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-wand-magic-sparkles"></i>}
                    {state.isProcessing ? 'Generating...' : 'Generate Reviewer'}
                  </button>
                </div>

                {/* Right Col: AI Config + Mobile Generate Button */}
                <div className="lg:col-span-4 space-y-6">
                  <ConfigPanel 
                    config={state.aiConfig} 
                    updateConfig={actions.updateConfig} 
                    isProcessing={state.isProcessing}
                  />

                  {/* Mobile Generate Reviewer Button (Visible only below lg, at the bottom) */}
                  <button 
                    type="submit" 
                    disabled={state.isProcessing} 
                    className="flex lg:hidden w-full bg-blue-600 text-white py-3.5 rounded-xl font-bold hover:shadow-lg hover:bg-blue-700 active:scale-[0.98] transition-all items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-md"
                  >
                    {state.isProcessing ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-wand-magic-sparkles"></i>}
                    {state.isProcessing ? 'Generating...' : 'Generate Reviewer'}
                  </button>
                </div>
              </form>
            )}

            {/* --- RESULTS VIEW --- */}
            {state.view === 'dashboard' && state.generatedData && (
              <ResultsDashboard 
                data={state.generatedData}
                config={state.aiConfig}
                onBack={handleBack}
                onSave={actions.handleSave}
                onDownload={actions.handleDownload}
                onUpdateQuiz={actions.updateGeneratedQuiz}
                originalContent={state.rawContent || state.textInput}
                isSaving={state.isSaving}
                isSaveDisabled={state.isSaving || state.hasSavedToDashboard}
              />
            )}

          </div>
        </div>
      </main>

      {/* Loading Overlay */}
      {state.isProcessing && (
        <GenerationProgress 
          loadingText={state.loadingText}
          step={state.generationStep}
          config={state.aiConfig}
        />
      )}
    </div>
  );
};

export default CreateReviewer;
